import { isRegisterWithImmatureNeighbourAtom } from 'app/atoms';
import { userInfoAtom } from 'app/atoms/auth';
import { useOnboarding } from 'app/hooks/use-onboarding';
import { Event, track } from 'app/lib/analytics';
import { useClosestOffersByTag } from 'app/network/listing';
import { useUpsertOnePreference } from 'app/network/preferences';
import type { SpecificNavigationProp } from 'app/types/navigation';
import type { SharerType } from 'app/types/preferences';
import { useAtomValue, useSetAtom } from 'jotai';
import { useState } from 'react';

export function useChooseSharerTypeFlow(navigation: SpecificNavigationProp<'choose-sharer-type'>) {
  const { goToNext } = useOnboarding();
  const userInfo = useAtomValue(userInfoAtom);
  const { mutate: upsertSharerTypePreferences, isPending } = useUpsertOnePreference('sharerType');
  const [selectedSharerType, setSelectedSharerType] = useState<SharerType | null>(null);
  const { refetch } = useClosestOffersByTag(undefined, { enabled: false });
  const setIsRegisterWithImmatureNeighbour = useSetAtom(isRegisterWithImmatureNeighbourAtom);

  const saveSharerTypePreferences = async () => {
    if (!selectedSharerType) return;

    const query = await refetch();
    const hasEnoughOffers = (query.data?.length ?? 0) >= 10;
    setIsRegisterWithImmatureNeighbour(!hasEnoughOffers);

    upsertSharerTypePreferences(selectedSharerType, {
      onSuccess: () => {
        track(Event.ChooseSharerType, { selected: selectedSharerType });
        if (userInfo?.acceptedGuidelineDate) {
          navigation.goBack();
        } else {
          goToNext();
        }
      },
    });
  };

  const onSelect = (type: SharerType) => {
    setSelectedSharerType(type);
  };

  return {
    isPending,
    isSharerTypeSelected: !!selectedSharerType,
    onSelect,
    saveSharerTypePreferences,
    selectedSharerType,
    userInfo,
  };
}
